import { IconButton, Typography } from '@mui/joy';
import { ArrowRight, X } from '@phosphor-icons/react';
import { FormattedMessage } from 'react-intl';

import { MultiLanguageSupport } from './MultiLanguageModal';

import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import QueryResolver from '~/services/api/query-resolver';
import { useWrappedMutation } from '~/services/api/rest-api';
import { UserService } from '~/services/api/users/endpoints';

export enum LocalizationAlertType {
  REPORT_FORM = 'report form',
  PROGRAM = 'program',
  SETTINGS_USER = 'settings user',
}

interface BasicLocalizationAlertProps {
  readonly message: string;
  readonly messageChild?: React.ReactNode;
  readonly alertPosition?: 'top' | 'bottom';
  readonly enableMultiLanguageSupportLink?: boolean;
}

interface LocalizationAlertProps {
  readonly type: LocalizationAlertType;
  readonly message: string;
}

export function BasicLocalizationAlert({
  message,
  messageChild,
  alertPosition = 'bottom',
  enableMultiLanguageSupportLink = true,

}: BasicLocalizationAlertProps) {
  const alertMargin = alertPosition === 'top' ? '!mb-4' : '!mt-4';

  return (
    <Card
      variant='soft'
      color='primary'
      className={`border-2 border-brand-primary-400 !rounded-3xl !py-2 ${alertMargin}`}
    >
      <div>
        <div className='flex justify-between items-center'>
          <Typography level='title-sm'>{message}</Typography>
          {messageChild}
        </div>
        {enableMultiLanguageSupportLink && (
          <div>
            <MultiLanguageSupport>
              <Button
                variant='plain'
                color='neutral'
                size='sm'
                endDecorator={<ArrowRight />}
              >
                <FormattedMessage defaultMessage='Proceed to multi-language support' />
              </Button>
            </MultiLanguageSupport>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function LocalizationAlert({
  type,
  message,
}: LocalizationAlertProps) {
  const userFlags = UserService.endpoints.flags.useQuery();

  const updateUserFags = useWrappedMutation(
    UserService.endpoints.updateUserFlags.useMutation(),
  );
  const updateVisitedFlag = async () => {
    const updatePayload = {
      default_property: 0,
      ...(type === LocalizationAlertType.SETTINGS_USER && {
        has_visited_user_settings: true,
      }),
      ...(type === LocalizationAlertType.REPORT_FORM && {
        has_visited_report_form_settings: true,
      }),
      ...(type === LocalizationAlertType.PROGRAM && {
        has_visited_program_settings: true,
      }),
    };

    await updateUserFags.mutateAsync(updatePayload);
  };

  return (
    <QueryResolver query={userFlags}>
      {(flags) => {
        if (
          (type === LocalizationAlertType.REPORT_FORM &&
            !flags.has_visited_report_form_settings) ||
          (type === LocalizationAlertType.PROGRAM &&
            !flags.has_visited_program_settings) ||
          (type === LocalizationAlertType.SETTINGS_USER &&
            !flags.has_visited_user_settings)
        ) {
          return (
            <BasicLocalizationAlert
              alertPosition='bottom'
              message={message}
              messageChild={
                <IconButton size='sm' onClick={updateVisitedFlag}>
                  <X />
                </IconButton>
              }
            />
          );
        }
      }}
    </QueryResolver>
  );
}
